import Auth from "@aws-amplify/auth"
import { CognitoUser } from "amazon-cognito-identity-js"
import * as React from "react"
import {
    Maybe,
    MerchantInfoQueryMibLocations,
    MibUserQueryMibUser,
    UpdateMibUserMutationUpdateMibUser,
} from "../../graphql/generated-types"
import { getClientWlDomainData } from "../../server/routes/utils"
import { envVars } from "../../utils/config"
import { currentAuthenticatedUser } from "../screens/auth/cognito"
import { getHotjarUserId } from "./utils"

interface State {
    isInitialized: boolean
    cognitoUserData: CognitoUser
    mibUser: Maybe<MibUserQueryMibUser>
    hotjarUserId: Maybe<string>
}

export enum AuthProvider {
    Facebook = "facebook",
    Google = "google",
    Cognito = "cognito",
}

export interface AuthInfoContextInterface {
    userId: string
    name: string
    isLoggedIn: boolean
    userEmail: string
    userPhoto?: string // Only for external auth (FB/Google)
    accessToken: string
    provider: AuthProvider
    isInitialized: boolean
    mibUser: Maybe<MibUserQueryMibUser>
    hotjarUserId: Maybe<string>
    setMibUserData: (mibUser: MibUserQueryMibUser | UpdateMibUserMutationUpdateMibUser) => void
    checkIsAuthenticated: (callback?: Function) => void
    configureCognitoAmplify: (locationInfo: Maybe<MerchantInfoQueryMibLocations>) => void
}

export const AuthInfoContext = React.createContext<AuthInfoContextInterface | null>(null)

class AuthInfoProvider extends React.Component<{}, State> {
    isMounted = false
    amplifyConfigured = false

    state = {
        isInitialized: false,
        cognitoUserData: null,
        mibUser: null,
        hotjarUserId: null,
    }

    obtainHotjarUserIdTimer = null

    safeSetState = (state, callback?: Function) => {
        if (this.isMounted) {
            return this.setState(state, () => {
                if (callback) {
                    callback()
                }
            })
        }
    }

    componentDidMount() {
        this.isMounted = true

        this.tryObtainHotjarUserId()
        this.obtainHotjarUserIdTimer = setInterval(this.tryObtainHotjarUserId, 2000)
    }

    componentWillUnmount() {
        this.isMounted = false

        if (this.obtainHotjarUserIdTimer) {
            clearInterval(this.obtainHotjarUserIdTimer)
        }
    }

    configureCognitoAmplify = (locationInfo: Maybe<MerchantInfoQueryMibLocations>) => {
        if (!this.amplifyConfigured) {
            let identityPoolId = envVars.COGNITO_IDENTITY_POOL_ID
            const isWhitelabeledDomain = !!getClientWlDomainData()

            if (
                isWhitelabeledDomain &&
                locationInfo &&
                locationInfo.apiKeys &&
                locationInfo.apiKeys.cognitoIdentityPoolId
            ) {
                identityPoolId = locationInfo.apiKeys.cognitoIdentityPoolId
            }
            Auth.configure({
                identityPoolId: identityPoolId,
                region: "us-east-1",
                identityPoolRegion: "us-east-1",
                userPoolId: envVars.COGNITO_USER_POOL_ID,
                userPoolWebClientId: envVars.COGNITO_CLIENT_ID,
            })
            this.amplifyConfigured = true
            this.handleCheckIsAuthenticated()
        }
    }

    tryObtainHotjarUserId = () => {
        const userId = getHotjarUserId()
        if (userId) {
            this.setState({ hotjarUserId: userId })
            clearInterval(this.obtainHotjarUserIdTimer)
        }
    }

    handleCheckIsAuthenticated = (callback?: Function) => {
        if (this.amplifyConfigured) {
            currentAuthenticatedUser()
                .then(userData => {
                    this.safeSetState({ cognitoUserData: userData, isInitialized: true }, callback)
                })
                .catch(() => this.safeSetState({ cognitoUserData: null, isInitialized: true, mibUser: null }))
        }
    }

    setMibUserData = (mibUser: MibUserQueryMibUser) => this.setState({ mibUser: mibUser })

    render() {
        const { cognitoUserData } = this.state

        let contextContent: AuthInfoContextInterface = {
            userId: null,
            userEmail: null,
            name: null,
            isLoggedIn: false,
            provider: null,
            accessToken: null,
            mibUser: this.state.mibUser,
            checkIsAuthenticated: this.handleCheckIsAuthenticated,
            configureCognitoAmplify: this.configureCognitoAmplify,
            setMibUserData: this.setMibUserData,
            isInitialized: this.state.isInitialized,
            hotjarUserId: this.state.hotjarUserId,
        }

        if (cognitoUserData && cognitoUserData.attributes) {
            // Authed with Email (email/password)
            contextContent = {
                ...contextContent,
                userId: cognitoUserData.username,
                userEmail: cognitoUserData.attributes.email,
                name: cognitoUserData.attributes.name,
                isLoggedIn: true,
                accessToken: cognitoUserData.signInUserSession.idToken.jwtToken,
                provider: AuthProvider.Cognito,
            }
        } else if (cognitoUserData && cognitoUserData.provider === AuthProvider.Google) {
            contextContent = {
                ...contextContent,
                userId: cognitoUserData.userId,
                userEmail: cognitoUserData.userEmail,
                name: cognitoUserData.name,
                userPhoto: cognitoUserData.userPhoto,
                isLoggedIn: true,
                accessToken: cognitoUserData.accessToken,
                provider: cognitoUserData.provider,
            }
        } else if (cognitoUserData && cognitoUserData.provider === AuthProvider.Facebook) {
            contextContent = {
                ...contextContent,
                userId: cognitoUserData.userId,
                userEmail: cognitoUserData.userEmail,
                name: cognitoUserData.name,
                userPhoto: cognitoUserData.userPhoto,
                isLoggedIn: true,
                accessToken: cognitoUserData.accessToken,
                provider: cognitoUserData.provider,
            }
        }

        return <AuthInfoContext.Provider value={contextContent}>{this.props.children}</AuthInfoContext.Provider>
    }
}

const AuthInfoConsumer = AuthInfoContext.Consumer

export { AuthInfoConsumer, AuthInfoProvider }
